import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Heart,
  MessageCircle,
  Bookmark,
  Send,
  MoreHorizontal,
} from 'lucide-react';
import { Avatar } from '@/shared/ui/Avatar';
import { SmartImage } from '@/shared/ui/SmartImage';
import { useStore } from '@/shared/state/store';
import { getPostById, getUserById } from '@/shared/data';
import { formatCount, formatRelativeTime } from '@/shared/utils/format';
import { cn } from '@/shared/utils/cn';

export function PostCard({ postId }: { postId: string }) {
  const post = getPostById(postId);
  const author = post ? getUserById(post.authorId) : undefined;
  const liked = useStore((s) => s.likedPostIds.has(postId));
  const saved = useStore((s) => s.savedPostIds.has(postId));
  const toggleLike = useStore((s) => s.togglePostLike);
  const toggleSave = useStore((s) => s.togglePostSave);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const scrollerRef = useRef<HTMLDivElement>(null);
  if (!post || !author) return null;

  const isCarousel = post.kind === 'carousel';

  // Update active index from scroll position. We use a small rAF debounce
  // because scroll events fire frequently during swipe.
  const onScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    const idx = Math.round(el.scrollLeft / el.clientWidth);
    if (idx !== carouselIndex) setCarouselIndex(idx);
  };

  return (
    <article data-testid="post-card" data-post-id={post.id} className="mb-2">
      <header className="flex items-center justify-between px-4 py-3">
        <Link
          to={`/instaflix/profile/${author.username}`}
          className="flex items-center gap-3 active:scale-95 transition-transform"
        >
          <Avatar
            src={author.avatar}
            alt={author.username}
            size={36}
            ring={author.verified ? 'rose' : 'none'}
          />
          <div>
            <div className="text-sm text-white/90">@{author.username}</div>
            {post.location && (
              <div className="text-[10px] text-white/40 tracking-wider">
                {post.location}
              </div>
            )}
          </div>
        </Link>
        <button
          aria-label="More"
          className="text-white/40 active:scale-90 transition-transform"
        >
          <MoreHorizontal className="w-5 h-5" />
        </button>
      </header>

      {isCarousel ? (
        <div className="relative w-full aspect-square bg-black/30">
          {/* Native horizontal scroll-snap container — handles touch swipe
              without any custom gesture code. Each slide is a full-width
              snap target. Scroll position drives carouselIndex (for dots).
              IMPORTANT: do NOT set touch-action: pan-x. That value FORBIDS
              vertical pan on this element, which froze page scroll whenever
              a carousel post filled the viewport. The default `auto` lets
              the browser disambiguate by gesture direction: horizontal drag
              → carousel; vertical drag → page scroll. */}
          <div
            ref={scrollerRef}
            onScroll={onScroll}
            className="h-full w-full overflow-x-auto snap-x snap-mandatory scrollbar-hide flex overscroll-x-contain"
            data-testid="post-carousel-scroller"
          >
            {post.media.map((src, i) => (
              <div
                key={i}
                className="relative shrink-0 w-full h-full snap-start snap-always"
              >
                <SmartImage fill src={src} alt={`${post.caption} (${i + 1}/${post.media.length})`} />
              </div>
            ))}
          </div>
          {/* dot indicators */}
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 pointer-events-none">
            {post.media.map((_, i) => (
              <span
                key={i}
                className={cn(
                  'rounded-full transition-all duration-200',
                  i === carouselIndex ? 'w-2 h-2 bg-white' : 'w-1.5 h-1.5 bg-white/40',
                )}
              />
            ))}
          </div>
          {/* counter chip */}
          <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-black/50 backdrop-blur-sm text-white/90 text-[10px] font-medium pointer-events-none">
            {carouselIndex + 1} / {post.media.length}
          </div>
        </div>
      ) : (
        <Link
          to={`/instaflix/post/${post.id}`}
          className="block relative w-full aspect-square bg-black/30"
        >
          <SmartImage fill src={post.media[0]} alt={post.caption} />
        </Link>
      )}

      <div className="px-4 pt-3 flex items-center gap-5">
        <button
          onClick={() => toggleLike(post.id)}
          aria-label="Like"
          className="active:scale-90 transition-transform"
        >
          <Heart
            className={cn(
              'w-6 h-6',
              liked ? 'fill-rose-500 text-rose-500' : 'text-white/80',
            )}
          />
        </button>
        <Link to={`/instaflix/post/${post.id}`} aria-label="Comment">
          <MessageCircle className="w-6 h-6 text-white/80" />
        </Link>
        <button
          aria-label="Share"
          className="active:scale-90 transition-transform"
        >
          <Send className="w-6 h-6 text-white/80" />
        </button>
        <button
          onClick={() => toggleSave(post.id)}
          aria-label="Save"
          className="ml-auto active:scale-90 transition-transform"
        >
          <Bookmark
            className={cn(
              'w-6 h-6',
              saved ? 'fill-amber-200 text-amber-200' : 'text-white/80',
            )}
          />
        </button>
      </div>

      <div className="px-4 pt-2 text-sm">
        <span className="text-white/90 font-medium">
          {formatCount(post.likesCount + (liked ? 1 : 0))} likes
        </span>
      </div>
      <div className="px-4 pt-1 text-sm text-white/80 line-clamp-2">
        <Link
          to={`/instaflix/profile/${author.username}`}
          className="font-medium text-white/90"
        >
          @{author.username}
        </Link>{' '}
        {post.caption}
      </div>
      <div className="px-4 pt-1 text-[11px] text-white/40 uppercase tracking-wider mb-3">
        {formatRelativeTime(post.createdAt)}
      </div>
    </article>
  );
}
